import { getCurrentLanguage } from "../i18n";
import { DATE_TIME_FORMATS, type DateTimeFormatName } from "./catalog";
import { ISO_DATE_RE, toIntlDate } from "./isoDate";
import type { IsoTimestamp, IsoDate } from "./isoTypes";

/** en-sloth is not a real BCP-47 locale; render its dates as en-US. */
export const resolveIntlLocale = (language: string): string =>
  language === "en-sloth" ? "en-US" : language;

export interface FormatDateTimeOptions {
  /** Required for timestamps; ignored for date-only values. */
  timeZone?: string;
  /** Defaults to the active app language. Pass explicitly only in tests. */
  locale?: string;
}

export const formatDateTime = (
  value: IsoTimestamp | IsoDate,
  formatName: DateTimeFormatName,
  options: FormatDateTimeOptions = {},
): string => {
  const intlOptions = DATE_TIME_FORMATS[formatName];
  if (!intlOptions) {
    throw new Error(`Unknown date format name: "${formatName}"`);
  }

  const language = options.locale ?? getCurrentLanguage();
  const intlLocale = resolveIntlLocale(language);

  // Date-only: treat as a calendar date, format at local noon (no tz conversion).
  // Noon keeps the calendar date stable regardless of the runtime timezone.
  if (ISO_DATE_RE.test(value)) {
    const localNoon = toIntlDate(value);
    return new Intl.DateTimeFormat(intlLocale, intlOptions).format(localNoon);
  }

  // Timestamp: require a timezone. Intl applies the timeZone option to the raw
  // instant directly — do NOT pre-shift the Date (that would double-apply the offset).
  if (!options.timeZone) {
    throw new Error(`formatDateTime requires a timeZone for timestamp value "${value}"`);
  }
  return new Intl.DateTimeFormat(intlLocale, {
    ...intlOptions,
    timeZone: options.timeZone,
  }).format(new Date(value));
};
